import React, { useState, useCallback } from 'react';
import { View, TouchableOpacity, Text, StyleSheet } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout } from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useTagManagement } from '@/hooks/admin/useTagManagement';
import { TagList } from '@/components/admin/corkBoard/TagList';
import { TagStatsStrip } from '@/components/admin/corkBoard/TagStatsStrip';
import { TagFormModal } from '@/components/admin/corkBoard/TagFormModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { showConfirm } from '@/utils/common/alert';
import { DesktopTagManagementView } from './desktop/DesktopTagManagementView';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

type TagManagementScreenProps = StackScreenProps<AdminStackParamList, 'TagManagement'>;

export const TagManagementScreen: React.FC<TagManagementScreenProps> = ({ navigation }) => {
  const { layoutMode } = useResponsive();
  const tagManagement = useTagManagement();
  const { redesign, semantic } = useAdminColors();

  const [formVisible, setFormVisible] = useState(false);
  const [editingTag, setEditingTag] = useState<CorkBoardTag | null>(null);

  const handleOpenCreate = useCallback(() => {
    setEditingTag(null);
    setFormVisible(true);
  }, []);

  const handleOpenEdit = useCallback((tag: CorkBoardTag) => {
    setEditingTag(tag);
    setFormVisible(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormVisible(false);
    setEditingTag(null);
  }, []);

  const handleSave = useCallback(
    async (data: { name: string; color: string | null; icon: string | null }) => {
      const payload = {
        name: data.name,
        color: data.color,
        icon: data.icon,
      };
      let success: boolean;
      if (editingTag) {
        success = await tagManagement.handleUpdateTag(editingTag.id, payload);
      } else {
        success = await tagManagement.handleCreateTag(payload);
      }
      if (success) handleCloseForm();
    },
    [editingTag, tagManagement.handleUpdateTag, tagManagement.handleCreateTag, handleCloseForm],
  );

  const handleDelete = useCallback(
    async (tag: CorkBoardTag) => {
      const confirmed = await showConfirm(
        'Delete Tag',
        `Are you sure you want to delete "${tag.name}"? Threads using this tag will keep their other tags.`,
        'Delete',
        'Cancel',
      );
      if (confirmed) {
        await tagManagement.handleDeleteTag(tag.id);
      }
    },
    [tagManagement.handleDeleteTag],
  );

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="TagManagementDesktop">
        <DesktopTagManagementView
          tagManagement={tagManagement}
          formVisible={formVisible}
          editingTag={editingTag}
          onOpenCreate={handleOpenCreate}
          onOpenEdit={handleOpenEdit}
          onCloseForm={handleCloseForm}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="TagManagement">
      <BaseAdminScreen
        screenName="TagManagement"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Tags"
          emptyStateType="corkBoardTags"
          onAddPress={handleOpenCreate}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
          disableScrollView={true}
        >
          {tagManagement.error && (
            <View
              style={[
                styles.errorBanner,
                {
                  backgroundColor: redesign.accent.redBg,
                  borderBottomColor: semantic.status.error,
                },
              ]}
            >
              <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
                Failed to load tags.
              </Text>
              <TouchableOpacity onPress={tagManagement.refetch}>
                <Text style={[styles.errorRetryText, { color: semantic.status.error }]}>
                  Retry
                </Text>
              </TouchableOpacity>
            </View>
          )}

          <TagStatsStrip tags={tagManagement.tags} />

          <TagList
            tags={tagManagement.tags}
            isLoading={tagManagement.isLoading}
            onToggleEnabled={tagManagement.handleToggleEnabled}
            onEdit={handleOpenEdit}
            onDelete={handleDelete}
            onMoveUp={tagManagement.handleMoveUp}
            onMoveDown={tagManagement.handleMoveDown}
            onRefresh={tagManagement.refetch}
            isRefreshing={tagManagement.isFetching}
          />
        </AdminEmptyScreenLayout>

        <TagFormModal
          visible={formVisible}
          tag={editingTag}
          isSaving={tagManagement.isCreating || tagManagement.isUpdating}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  errorBannerText: {
    fontSize: 13,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
